/*
  The side cart.

  Progressive enhancement, not a rewrite. The product form already works
  without JavaScript: it posts to /cart/add and Shopify redirects to the
  cart page. This intercepts that submit, posts the same FormData to
  /cart/add.js instead, and slides the drawer open. If the script never
  loads, or throws, the form submits normally and the buyer still gets
  their book — which matters more on a page selling a $5,500 edition
  than any animation does.

  Line quantities and removal post to /cart/change.js by LINE NUMBER
  rather than variant id. Two lines can share a variant when their
  properties differ — the same Patron level given once anonymously and
  once with a name to print — and changing by id would silently hit
  whichever came first.

  Everything is native: <dialog> for the drawer and focus trapping,
  fetch for the cart API. No library.
*/
(() => {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return;

  const body = drawer.querySelector('[data-cart-body]');
  const foot = drawer.querySelector('[data-cart-foot]');
  const countEls = document.querySelectorAll('[data-cart-count]');
  const money = (cents) =>
    (cents / 100).toLocaleString(undefined, {
      style: 'currency',
      currency: drawer.dataset.currency || 'USD',
    });

  /* The element that had focus before the drawer opened, so it can be
     given back afterwards rather than dumping the user at the top. */
  let opener = null;
  let busy = false;

  function open(triggeredBy) {
    opener = triggeredBy || document.activeElement;
    if (!drawer.open) drawer.showModal();
    /* Force a style flush between showModal() and the class, so the
       browser commits translateX(100%) first and has something to
       transition from. Reading offsetWidth does it synchronously.

       This was a double requestAnimationFrame at first. rAF does not
       fire in a background or throttled tab, and the drawer then opened
       already-off-screen with no way back — a transition that silently
       depends on the tab being focused is not a transition. */
    void drawer.offsetWidth;
    drawer.classList.add('cd--in');
    drawer.querySelector('[data-cart-close]')?.focus();
  }

  function close() {
    if (!drawer.open) return;
    drawer.classList.remove('cd--in');

    /* Wait for the slide out before leaving the top layer, or the panel
       vanishes instantly and only the backdrop appears to fade. */
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      drawer.removeEventListener('transitionend', onEnd);
      drawer.close();
      opener?.focus?.();
    };
    const onEnd = (e) => {
      if (e.target === drawer && e.propertyName === 'transform') finish();
    };
    drawer.addEventListener('transitionend', onEnd);
    /* Belt and braces: if the transition never fires — reduced motion,
       a background tab, a browser that drops it — the drawer still
       closes rather than sticking open. */
    setTimeout(finish, 400);
  }

  /* Escape fires `cancel`, which would close without the animation. */
  drawer.addEventListener('cancel', (e) => {
    e.preventDefault();
    close();
  });

  drawer.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-close]')) {
      close();
      return;
    }
    /* The dialog IS the panel, so a backdrop click still reports the
       dialog as its target — but lands outside its box. Compare against
       the rect rather than the target. */
    if (e.target === drawer) {
      const r = drawer.getBoundingClientRect();
      const outside =
        e.clientX < r.left || e.clientX > r.right ||
        e.clientY < r.top || e.clientY > r.bottom;
      if (outside) close();
    }
  });

  async function refresh() {
    /* /cart.js is the whole cart as JSON. An earlier version asked for a
       section render here, which returns HTML — res.json() threw, the
       submit handler's catch fired, and every add fell back to a full
       page load. The drawer never appeared and nothing looked broken,
       which is the worst kind of bug. */
    const root = window.Shopify?.routes?.root || '/';
    const res = await fetch(`${root.replace(/\/$/, '')}/cart.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`cart.js ${res.status}`);
    const cart = await res.json();
    render(cart);
    return cart;
  }

  /* Change one line's quantity. 0 removes it. */
  async function changeLine(line, quantity) {
    if (busy) return;
    busy = true;
    drawer.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ line, quantity }),
      });
      if (!res.ok) throw new Error(`change ${res.status}`);
      render(await res.json());
    } catch {
      /* Whatever went wrong, the server is the truth. Re-read rather
         than leaving the panel showing a quantity that was never
         applied. */
      await refresh().catch(() => {});
    } finally {
      busy = false;
      drawer.removeAttribute('aria-busy');
    }
  }

  function render(cart) {
    countEls.forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });

    if (!body) return;

    if (!cart.items || cart.items.length === 0) {
      body.innerHTML = '<p class="cd__empty">Your cart is empty.</p>';
      if (foot) foot.hidden = true;
      return;
    }

    const rows = cart.items
      .map((item, i) => {
        const line = i + 1; // /cart/change.js counts lines from 1
        /* On a limited edition the variant title IS the price band, so
           it is stated rather than tucked away — a cart that hides it is
           ambiguous about which band was bought. */
        const variant =
          item.variant_title && !/default/i.test(item.variant_title)
            ? `<p class="cd__variant">${esc(item.variant_title)}</p>`
            : '';
        const plan = item.selling_plan_allocation
          ? `<p class="cd__plan">${esc(
              item.selling_plan_allocation.selling_plan.name
            )}</p>`
          : '';
        const img = item.image
          ? `<img class="cd__thumb" src="${esc(
              item.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2')
            )}" alt="" width="56" loading="lazy">`
          : '<span class="cd__thumb cd__thumb--none"></span>';

        /* A subscription line is a commitment, not a quantity — nobody
           holds two of the same membership — so it gets removal only. */
        const stepper = item.selling_plan_allocation
          ? `<p class="cd__qty">${money(item.original_price)}</p>`
          : `<div class="cd__step">
               <button type="button" class="cd__stepbtn" data-line="${line}" data-qty="${
                 item.quantity - 1
               }" aria-label="Reduce quantity of ${esc(item.product_title)}">&minus;</button>
               <span class="cd__stepval">${item.quantity}</span>
               <button type="button" class="cd__stepbtn" data-line="${line}" data-qty="${
                 item.quantity + 1
               }" aria-label="Increase quantity of ${esc(item.product_title)}">+</button>
             </div>`;

        return `
          <li class="cd__item">
            ${img}
            <div class="cd__detail">
              <a class="cd__title" href="${esc(item.url)}">${esc(
                item.product_title
              )}</a>
              ${variant}
              ${plan}
              ${stepper}
              <button type="button" class="cd__remove" data-line="${line}" data-qty="0">Remove</button>
            </div>
            <p class="cd__line">${money(item.original_line_price)}</p>
          </li>`;
      })
      .join('');

    body.innerHTML = `<ul class="cd__items" role="list">${rows}</ul>`;

    if (foot) {
      foot.hidden = false;
      foot.innerHTML = `
        <p class="cd__subtotal">
          <span>Subtotal</span>
          <span>${money(cart.total_price)}</span>
        </p>
        <p class="cd__note">Shipping and any taxes are calculated at checkout.</p>
        <a class="button cd__checkout" href="/checkout">Checkout</a>
        <a class="cd__viewcart" href="/cart">View cart</a>`;
    }
  }

  /* One delegated handler for every quantity control, because the rows
     are replaced wholesale on each render. */
  drawer.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-line]');
    if (!btn) return;
    changeLine(Number(btn.dataset.line), Math.max(0, Number(btn.dataset.qty)));
  });

  function esc(s) {
    return String(s ?? '').replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
  }

  /* Intercept every product form on the page. */
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!form.matches('form[action*="/cart/add"]')) return;

    /* Let the browser enforce required fields — counsel's authorisation
       checkbox among them — before anything is sent. */
    if (!form.reportValidity()) return;

    e.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const label = button?.textContent;
    if (button) {
      button.disabled = true;
      button.textContent = 'Adding…';
    }

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });

      if (!res.ok) {
        /* Out of stock, or a band that sold out while the page sat open.
           Shopify explains it better than we can, so show its message
           and let the buyer choose another band. */
        const err = await res.json().catch(() => null);
        alertInline(form, err?.description || err?.message || 'That could not be added.');
        return;
      }

      await refresh();
      open(button);
    } catch {
      /* Network gone. Fall back to the real submit rather than
         swallowing the click. */
      form.submit();
    } finally {
      if (button) {
        button.disabled = false;
        if (label) button.textContent = label;
      }
    }
  });

  function alertInline(form, message) {
    let el = form.querySelector('[data-cart-error]');
    if (!el) {
      el = document.createElement('p');
      el.setAttribute('data-cart-error', '');
      el.setAttribute('role', 'status');
      el.className = 'pd__error';
      form.appendChild(el);
    }
    el.textContent = message;
  }

  /* The header cart icon opens the drawer rather than navigating. */
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-cart-open]');
    if (!trigger) return;
    e.preventDefault();
    refresh().then(() => open(trigger));
  });
})();
